import React, { useEffect, useState } from 'react'

import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { Box, Paper, Stack, Typography } from '@mui/material'
import axios from 'axios'

type ScheduleProps = {
  item: any
  agama: string
}

type AcaraItem = {
  key: string
  label: string
  icon: React.ReactNode
  show: boolean
  value: string
  lokasi?: string
  keterangan?: string
}

type DukaAcara = {
  ID_UNIT: string
  NO_REG: string
  URUT: number
  JUDUL: string
  ISIAN: string
}

const ObituarySchedule = ({ item, agama }: ScheduleProps) => {
  const [acaraData, setAcaraData] = useState<DukaAcara[]>([])
  const urlAPI = process.env.NEXT_PUBLIC_API_URL || ''

  useEffect(() => {
    const fetchAcara = async () => {
      try {
        if (!item) return

        const kumpulanAcara: DukaAcara[] = []

        const jsonArray = {
          idunit: item.ID_UNIT,
          noreg: item.NO_REG
        }

        const response = await axios.post(urlAPI + 'registrasi/dukaacaraselect', jsonArray)

        // Mengakses data dari format respon yang benar
        if (response.data && response.data.response && Array.isArray(response.data.response.list)) {
          // Menggunakan forEach untuk setiap item dalam list
          response.data.response.list.forEach((acaraItem: DukaAcara) => {
            kumpulanAcara.push(acaraItem)
          })

          setAcaraData(kumpulanAcara)
        }
      } catch (error) {
        console.error('Gagal mengambil data acara:', error)
      }
    }

    fetchAcara()
  }, [item, urlAPI])

  const renderAcara = (): AcaraItem[] => {
    if (acaraData && acaraData.length > 0) {
      const sortedAcara = [...acaraData].sort((a, b) => {
        return a.URUT - b.URUT
      })

      const apiAcara: AcaraItem[] = sortedAcara.map(item => ({
        key: `api_${item.URUT || Math.random()}`,
        label: item.JUDUL || 'Acara',
        icon: (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: 'white',
              display: 'inline-block'
            }}
          />
        ),
        show: true,
        value: item.ISIAN || '',
        lokasi: undefined,
        keterangan: undefined
      }))

      return apiAcara
    }

    return []
  }

  // Tampilkan hanya jika ada jadwal
  const hasSchedule = acaraData && acaraData.length > 0

  if (!hasSchedule) return null

  return (
    <Paper
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, #5DAEDA 0%, #2887bf 100%)',
        color: 'white',
        p: 4,
        borderRadius: 4,
        mb: 4,
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 12px 40px rgba(21, 101, 192, 0.3), 0 4px 16px rgba(0, 0, 0, 0.1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -20,
          right: -20,
          width: 120,
          height: 120,
          background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 50%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 80,
          height: 80,
          background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 60%)',
          borderRadius: '50%',
          animation: 'float 8s ease-in-out infinite reverse'
        },
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px) scale(1)' },
          '50%': { transform: 'translateY(-10px) scale(1.05)' }
        }
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 26, color: 'white' }} />
          </Box>
          <Typography
            variant='h6'
            sx={{
              fontWeight: 800,
              fontSize: '1.2rem',
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              textShadow: '0 2px 8px rgba(0,0,0,0.4)',
              color: 'white'
            }}
          >
            Jadwal Acara
          </Typography>
          <Box
            sx={{
              width: 80,
              height: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: 2,
              mx: 'auto',
              mt: 2,
              boxShadow: '0 2px 8px rgba(255, 255, 255, 0.3)'
            }}
          />
        </Box>
        <Stack spacing={3}>
          {renderAcara().map(a => (
            <Box
              key={a.key}
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)',
                borderRadius: 4,
                p: 3.5,
                border: '1px solid rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(15px)',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.12) 100%)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                {typeof a.icon === 'object' && React.isValidElement(a.icon) ? (
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    {a.icon}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginLeft: 1.5,
                      marginRight: 1.5
                    }}
                  >
                    {a.icon}
                  </Box>
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant='h6'
                    sx={{
                      fontWeight: 600,
                      mb: 1.5,
                      fontSize: '0.90rem',
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      color: 'white'
                    }}
                  >
                    {a.label}
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{
                      opacity: 0.95,
                      lineHeight: 1.6,
                      fontSize: '0.80rem',
                      fontWeight: 500,
                      color: 'rgba(255, 255, 255, 0.95)',
                      textShadow: '0 1px 3px rgba(0,0,0,0.2)'
                    }}
                  >
                    {a.value}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: '1px solid rgba(255, 255, 255, 0.3)',
            textAlign: 'center'
          }}
        >
          <Typography
            variant='caption'
            sx={{
              opacity: 0.9,
              fontSize: '0.8rem',
              fontStyle: 'italic',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)',
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 500
            }}
          >
            * Waktu dapat berubah sewaktu-waktu
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}

export default ObituarySchedule
